import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Upload, Phone, Video } from 'lucide-react';
import Button from '@/components/ui/Button';
import { profileService, postService, followService } from '@/services/api';
import { useAuth, isOnline } from '@/contexts/AuthContext';
import { useToast } from '@/contexts/ToastContext';
import { useCall } from '@/contexts/CallContext';
import { cn } from '@/lib/utils';
import { ROUTES } from '@/constants';
import PostCard from '@/components/feed/PostCard';
import { UserAvatar } from '@/components/ui/Avatar';

export default function ProfilePage() {
  const { username } = useParams<{ username: string }>();
  const { user, profile } = useAuth();
  // Routes use "/@handle", and "/@me" resolves to the signed-in user.
  const handle = username?.startsWith('@') ? username.slice(1) : username;
  const targetUsername = handle === 'me' ? profile?.username : handle;
  const { toast } = useToast();
  const navigate = useNavigate();
  const { startCall } = useCall();
  const [profileData, setProfileData] = useState<any>(null);
  const [posts, setPosts] = useState<any[]>([]);
  const [replyPosts, setReplyPosts] = useState<any[]>([]);
  const [repostPosts, setRepostPosts] = useState<any[]>([]);
  const [likedPosts, setLikedPosts] = useState<any[]>([]);
  const [tabLoaded, setTabLoaded] = useState<Record<string, boolean>>({ posts: true });
  const [loading, setLoading] = useState(true);
  const [isFollowing, setIsFollowing] = useState(false);
  const [activeTab, setActiveTab] = useState<'posts' | 'replies' | 'reposts' | 'liked'>('posts');
  const [followerCount, setFollowerCount] = useState(0);
  const [followingCount, setFollowingCount] = useState(0);
  const [postCount, setPostCount] = useState(0);
  const [editMode, setEditMode] = useState(false);
  const [editDisplayName, setEditDisplayName] = useState('');
  const [editBio, setEditBio] = useState('');
  const [editLocation, setEditLocation] = useState('');
  const [editWebsite, setEditWebsite] = useState('');

  useEffect(() => {
    if (!targetUsername) return;
    loadProfile();
  }, [targetUsername]);

  const loadProfile = async () => {
    if (!targetUsername) return;
    const { data, error } = await profileService.getProfileByUsername(targetUsername);
    if (error || !data) {
      toast('Profile not found', '', 'destructive');
      navigate(ROUTES.HOME);
      return;
    }
    setProfileData(data);
    setEditDisplayName(data.display_name || '');
    setEditBio(data.bio || '');
    setEditLocation(data.location || '');
    setEditWebsite(data.website || '');

    // Check follow status
    if (user) {
      const { data: followData } = await followService.isFollowing(data.id);
      setIsFollowing(!!followData);
    }

    // Load posts
    const { data: postsData } = await postService.getPostsByUser(data.id, 50);
    setPosts(postsData || []);
    setPostCount(postsData?.length || 0);

    // Load real follower/following counts
    const { data: counts } = await followService.getCounts(data.id);
    if (counts) {
      setFollowerCount(counts.followers);
      setFollowingCount(counts.following);
    }

    setLoading(false);
  };

  // Load replies / reposts / liked posts the first time their tab opens.
  useEffect(() => {
    if (!targetUsername) return;
    if (activeTab === 'replies' && !tabLoaded['replies'] && profileData?.id) {
      (async () => {
        const { data } = await postService.getRepliesByUser(profileData.id);
        setReplyPosts(data || []);
        setTabLoaded((prev) => ({ ...prev, replies: true }));
      })();
      return;
    }
    if (!user) return;
    if (activeTab === 'reposts' && !tabLoaded['reposts']) {
      (async () => {
        const { data } = await postService.getRepostedPosts();
        setRepostPosts(data || []);
        setTabLoaded((prev) => ({ ...prev, reposts: true }));
      })();
      return;
    }
    if (activeTab === 'liked' && !tabLoaded['liked']) {
      (async () => {
        const { data } = await postService.getLikedPosts();
        setLikedPosts(data || []);
        setTabLoaded((prev) => ({ ...prev, liked: true }));
      })();
    }
  }, [activeTab, targetUsername, user, tabLoaded, profileData?.id]);

  const handlePostDeleted = (deletedPostId: string) => {
    // Remove from whichever tab is currently open so the post vanishes
    // immediately without waiting for a refetch.
    if (activeTab === 'posts') {
      setPosts((prev) => prev.filter((p) => p.id !== deletedPostId));
      setPostCount((c) => Math.max(0, c - 1));
    } else if (activeTab === 'replies') {
      setReplyPosts((prev) => prev.filter((p) => p.id !== deletedPostId));
    } else if (activeTab === 'reposts') {
      setRepostPosts((prev) => prev.filter((p) => p.id !== deletedPostId));
    } else if (activeTab === 'liked') {
      setLikedPosts((prev) => prev.filter((p) => p.id !== deletedPostId));
    }
    // Also refresh the underlying data so the count stays accurate across tabs.
    void loadProfile();
  };

  const handleFollow = async () => {
    if (!user || !profileData) return;
    try {
      if (isFollowing) {
        await followService.unfollow(profileData.id);
        setIsFollowing(false);
        setFollowerCount((c) => Math.max(0, c - 1));
      } else {
        await followService.follow(profileData.id);
        setIsFollowing(true);
        setFollowerCount((c) => c + 1);
      }
    } catch (err: any) {
      toast('Failed', err.message, 'destructive');
    }
  };

  const handleSave = async () => {
    if (!user) return;
    try {
      await profileService.updateProfile(user.id, {
        display_name: editDisplayName || null,
        bio: editBio || null,
        location: editLocation || null,
        website: editWebsite || null,
      });
      setEditMode(false);
      toast('Profile updated');
      loadProfile();
    } catch (err: any) {
      toast('Failed', err.message, 'destructive');
    }
  };

  const isOwn = user && profileData && profileData.id === user.id;

  if (loading) return (
    <div className="flex h-64 items-center justify-center"><div className="skeleton-text h-8 w-32" /></div>
  );

  if (!profileData) return null;

  const websiteHref = profileData.website
    ? (profileData.website.startsWith('http') ? profileData.website : `https://${profileData.website}`)
    : null;

  return (
    <div className="max-w-2xl mx-auto">
      <div className="relative h-32 md:h-48 w-full rounded-2xl overflow-hidden bg-gradient-to-r from-brand-600 to-brand-400 mx-4 sm:mx-6 mt-4 sm:mt-6 mb-4 sm:mb-6">
        {profileData.cover_url && (
          <img src={profileData.cover_url} alt="Cover" className="object-cover w-full h-full" />
        )}
        {isOwn && (
          <button className="absolute top-3 right-3 rounded-full bg-white/90 dark:bg-neutral-900/90 p-2 shadow-lg text-neutral-600 dark:text-neutral-100" aria-label="Change cover">
            <Upload size={18} />
          </button>
        )}
      </div>

      <div className="px-3 sm:px-6">
        <div className="relative z-10 -mt-12 md:-mt-16 flex flex-wrap items-end justify-between gap-3">
          <div className="h-24 w-24 md:h-32 md:w-32 shrink-0 overflow-hidden rounded-full border-4 border-neutral-50 dark:border-neutral-950 bg-neutral-200 dark:bg-neutral-800">
            <UserAvatar
              src={profileData.avatar_url}
              size="xl"
              isOnline={isOnline(profileData)}
            />
          </div>
          <div className="ml-auto flex gap-2 pb-1">
            {isOwn ? (
              <Button onClick={() => navigate(ROUTES.SETTINGS)}>Edit profile</Button>
            ) : (
              <>
                <Button onClick={handleFollow} variant={isFollowing ? 'outline' : 'primary'}>
                  {isFollowing ? 'Following' : 'Follow'}
                </Button>
                <Button
                  variant="ghost"
                  onClick={() => navigate(`${ROUTES.MESSAGES}?with=${profileData.id}`)}
                >
                  Message
                </Button>
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => startCall(profileData, 'audio')}
                  aria-label={`Start voice call with ${profileData.display_name || profileData.username}`}
                  title="Voice call"
                >
                  <Phone size={18} />
                </Button>
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => startCall(profileData, 'video')}
                  aria-label={`Start video call with ${profileData.display_name || profileData.username}`}
                  title="Video call"
                >
                  <Video size={18} />
                </Button>
              </>
            )}
          </div>
        </div>

        <div className="mt-3">
          <h1 className="text-xl md:text-2xl font-bold break-words">
            {profileData.display_name || profileData.username}
          </h1>
          <p className="text-sm text-neutral-500 dark:text-neutral-400">@{profileData.username}</p>
          {profileData.bio && (
            <p className="mt-3 text-neutral-700 dark:text-neutral-300 break-words">{profileData.bio}</p>
          )}
          {(profileData.location || websiteHref) && (
            <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-neutral-500 dark:text-neutral-400">
              {profileData.location && <span className="truncate">{profileData.location}</span>}
              {websiteHref && (
                <a href={websiteHref} target="_blank" rel="noreferrer noopener" className="max-w-full truncate text-brand-600 dark:text-brand-400 hover:underline">
                  {profileData.website}
                </a>
              )}
            </div>
          )}
        </div>

        <div className="mt-4 flex gap-6 text-sm">
          <div className="flex items-baseline gap-1.5">
            <span className="font-semibold text-neutral-900 dark:text-neutral-100">{postCount}</span>
            <span className="text-neutral-500 dark:text-neutral-400">Posts</span>
          </div>
          <div className="flex items-baseline gap-1.5">
            <span className="font-semibold text-neutral-900 dark:text-neutral-100">{followerCount}</span>
            <span className="text-neutral-500 dark:text-neutral-400">Followers</span>
          </div>
          <div className="flex items-baseline gap-1.5">
            <span className="font-semibold text-neutral-900 dark:text-neutral-100">{followingCount}</span>
            <span className="text-neutral-500 dark:text-neutral-400">Following</span>
          </div>
        </div>

        <div className="mt-4 flex gap-1 border-b border-neutral-200 dark:border-neutral-800">
          {(['posts', 'replies', 'reposts', 'liked'] as const).map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={cn(
                'shrink-0 px-4 py-2.5 text-sm font-medium capitalize border-b-2 -mb-px transition-colors',
                activeTab === tab
                  ? 'border-brand-600 text-brand-600 dark:text-brand-400'
                  : 'border-transparent text-neutral-500 dark:text-neutral-400 hover:text-neutral-700 dark:hover:text-neutral-300',
              )}
            >
              {tab === 'liked' ? 'Liked' : tab}
            </button>
          ))}
        </div>

        <div className="mt-3 pb-4">
          {activeTab === 'posts' ? (
            posts.length === 0 ? (
              <p className="py-10 text-center text-neutral-500 dark:text-neutral-400">No posts yet</p>
            ) : (
              <div className="space-y-3">
                {posts.map((post) => (
                  <PostCard
                    key={post.id}
                    post={post}
                    onPostDeleted={() => handlePostDeleted(post.id)}
                  />
                ))}
              </div>
            )
          ) : activeTab === 'replies' ? (
            replyPosts.length === 0 ? (
              <p className="py-10 text-center text-neutral-500 dark:text-neutral-400">
                No replies yet
              </p>
            ) : (
              <div className="space-y-3">
                {replyPosts.map((post) => (
                  <PostCard
                    key={post.id}
                    post={post}
                    onPostDeleted={() => handlePostDeleted(post.id)}
                  />
                ))}
              </div>
            )
          ) : activeTab === 'reposts' ? (
            repostPosts.length === 0 ? (
              <p className="py-10 text-center text-neutral-500 dark:text-neutral-400">
                No reposts yet
              </p>
            ) : (
              <div className="space-y-3">
                {repostPosts.map((post) => (
                  <PostCard
                    key={post.id}
                    post={post}
                    onPostDeleted={() => handlePostDeleted(post.id)}
                  />
                ))}
              </div>
            )
          ) : (
            likedPosts.length === 0 ? (
              <p className="py-10 text-center text-neutral-500 dark:text-neutral-400">
                No liked posts yet
              </p>
            ) : (
              <div className="space-y-3">
                {likedPosts.map((post) => (
                  <PostCard
                    key={post.id}
                    post={post}
                    onPostDeleted={() => handlePostDeleted(post.id)}
                  />
                ))}
              </div>
            )
          )}
        </div>
      </div>
    </div>
  );
}

